import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useGame } from "@/context/GameContext";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;
const BG_IMAGE = "https://i.pinimg.com/1200x/29/05/43/290543ec25d14cebeab11f46e0ccaffb.jpg";

const EMOJI_STYLE = `
  .pricing-emoji {
    font-family: "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif;
    font-size: 1.2em;
    line-height: 1;
    vertical-align: middle;
    display: inline-block;
    margin-left: 6px;
    filter: none !important;
    opacity: 1 !important;
  }
  @keyframes tournamentPulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(241,225,148,0.4), 0 0 40px rgba(241,225,148,0.15), 0 10px 40px rgba(0,0,0,0.4); }
    50% { box-shadow: 0 0 0 8px rgba(241,225,148,0), 0 0 60px rgba(241,225,148,0.35), 0 10px 40px rgba(0,0,0,0.4); }
  }
  .tournament-highlight {
    animation: tournamentPulse 1.8s ease-in-out infinite;
    border: 2px solid rgba(241,225,148,0.8) !important;
  }
`;

const TOURNAMENT_PLANS = [
  { id: "tournament_4",  icon: "🥉", label: "4 فرق",   matches: 3,  price: "7.99",  original: "9.99",  rounds: ["نصف النهائي ×2", "النهائي ×1"] },
  { id: "tournament_8",  icon: "🥈", label: "8 فرق",   matches: 7,  price: "13.99", original: "16.99", rounds: ["ربع النهائي ×4", "نصف النهائي ×2", "النهائي ×1"], popular: true },
  { id: "tournament_16", icon: "🥇", label: "16 فريق", matches: 15, price: "19.99", original: "24.99", rounds: ["دور الـ16", "ربع النهائي", "نصف النهائي", "النهائي"] },
  { id: "tournament_32", icon: "👑", label: "32 فريق", matches: 31, price: "44.99", original: "54.99", rounds: ["دور الـ32", "دور الـ16", "ربع النهائي", "نصف النهائي", "النهائي"] },
];

const PLANS = [
  {
    id: "two_games",
    name: "لعبتان",
    price: "6.99",
    priceNum: 6.99,
    period: "",
    tagline: "مثالية لتجربة حُجّة أو لسهرة واحدة مع أصحابك.",
    cta: "اشترك الآن",
    features: [
      "🎮 مباراتان رسميتان كاملتان",
      "🌐 مباراتان في مجتمع حُجّة",
      "⚡️ بدون مدة — حتى استهلاك الرصيد",
      "✅ ابدأ اللعب فورًا",
    ],
  },
  {
    id: "biweekly",
    name: "أسبوعان",
    price: "17.99",
    priceNum: 17.99,
    period: "/ 14 يوم",
    badge: "⭐️ الأكثر شيوعًا",
    popular: true,
    tagline: "الخيار المناسب لجلسات متكررة مع أصحابك خلال أسبوعين.",
    cta: "اشترك الآن",
    features: [
      "🎮 10 مباريات رسمية خلال 14 يومًا",
      "🌐 50 مباراة في مجتمع حُجّة",
      "⭐️ حفظ الفئات في المفضلة",
      "💡 حوالي 1.80 ر.س للمباراة الرسمية",
    ],
  },
  {
    id: "monthly",
    name: "شهري",
    price: "29.99",
    priceNum: 29.99,
    period: "/ 30 يوم",
    tagline: "أفضل قيمة للمجموعات والجلسات المتكررة.",
    cta: "اشترك الآن",
    features: [
      "🎮 20 مباراة رسمية خلال 30 يومًا",
      "🌐 100 مباراة في مجتمع حُجّة",
      "⭐️ حفظ الفئات في المفضلة",
      "🔥 أفضل قيمة لكل مباراة",
      "💡 حوالي 1.50 ر.س للمباراة الرسمية",
    ],
  },
];

// All features verified against backend — only include what actually works for all paid plans
const SHARED_FEATURES = [
  { emoji: "📚", text: "وصول كامل إلى جميع فئات حُجّة" },
  { emoji: "🌍", text: "وصول كامل إلى مجتمع حُجّة وفئات اللاعبين" },
  { emoji: "🚀", text: "إنشاء ونشر فئاتك الخاصة" },
  { emoji: "🧠", text: "جميع وسائل المساعدة" },
  { emoji: "💾", text: "حفظ المباراة واستكمالها لاحقًا" },
  { emoji: "✨", text: "تحديثات مستمرة للأسئلة والفئات" },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userToken, isPremium } = useGame();
  const [loading, setLoading]           = useState(null);
  const [showForm, setShowForm]         = useState(null);
  const [clientName, setClientName]     = useState("");
  const [clientMobile, setClientMobile] = useState("");
  // Affiliate discount code
  const [affiliateInput, setAffiliateInput]   = useState("");
  const [affiliateStatus, setAffiliateStatus] = useState(null); // null|"checking"|"applied"|"invalid"
  const [affiliateData, setAffiliateData]     = useState(null); // { discount_code, customer_discount_percent }
  const [affiliateLoading, setAffiliateLoading] = useState(false);
  const [openFaq, setOpenFaq]           = useState(0);
  const tournamentRef = useRef(null);

  // highlightTournament can be a planId string (e.g. "tournament_8") OR true (generic scroll)
  const highlightRaw = location.state?.highlightTournament || null;
  const highlightPlanId = highlightRaw && highlightRaw !== true ? highlightRaw : null;
  const shouldScroll   = !!highlightRaw;
  useEffect(() => {
    if (shouldScroll && tournamentRef.current) {
      setTimeout(() => {
        tournamentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openForm = (planId) => {
    if (!currentUser) { toast.info("سجّل دخولك أولاً للاشتراك"); navigate("/login"); return; }
    // Store so PaymentSuccessPage knows to redirect back to tournament setup
    if (planId.startsWith("tournament_")) {
      localStorage.setItem("pending_tournament_plan", planId);
    }
    setAffiliateInput("");
    setAffiliateStatus(null);
    setAffiliateData(null);
    setShowForm(planId);
  };

  const handleApplyCode = async () => {
    const code = affiliateInput.trim();
    if (!code) return;
    setAffiliateLoading(true);
    setAffiliateStatus("checking");
    try {
      const { data } = await axios.get(`${API}/affiliate/validate`, { params: { code } });
      setAffiliateData(data);
      setAffiliateStatus("applied");
    } catch {
      setAffiliateData(null);
      setAffiliateStatus("invalid");
    } finally {
      setAffiliateLoading(false);
    }
  };

  const handleRemoveCode = () => {
    setAffiliateInput("");
    setAffiliateStatus(null);
    setAffiliateData(null);
  };

  const handleUpgrade = async () => {
    // ── Tournament plans: direct Salla redirect — no name/mobile needed ──
    if (showForm?.startsWith("tournament_")) {
      const TOURNAMENT_SALLA_URLS = {
        tournament_4:  "https://salla.sa/hujjahgame/EXoWqQD",
        tournament_8:  "https://salla.sa/hujjahgame/pAglWgq",
        tournament_16: "https://salla.sa/hujjahgame/BpogAaw",
        tournament_32: "https://salla.sa/hujjahgame/EXoWqwV",
      };
      const sallaUrl = TOURNAMENT_SALLA_URLS[showForm];
      if (sallaUrl) {
        toast.success("جاري التحويل لصفحة الدفع...");
        window.location.href = sallaUrl;
        return;
      }
    }

    // ── Subscription plans → Salla initiate ──
    setLoading(showForm);
    try {
      const { data } = await axios.post(
        `${API}/salla/initiate`,
        { plan_id: showForm },
        { headers: { Authorization: `Bearer ${userToken}` } }
      );
      localStorage.setItem("salla_purchase_id", data.purchase_id);
      toast.success("جاري التحويل لصفحة الدفع...");
      window.location.href = data.salla_url;
    } catch (err) {
      const msg = err?.response?.data?.detail || "خطأ في بدء عملية الدفع";
      toast.error(msg);
    } finally {
      setLoading(null);
    }
    // Tamara (preserved — not used by any active plan)
    // try { ... tamara checkout ... } — kept in git history
  };

  const gameCredits      = currentUser?.game_credits || 0;
  const communityCredits = currentUser?.community_game_credits || 0;
  const selectedPlan     = PLANS.find(p => p.id === showForm);
  const selectedTP       = TOURNAMENT_PLANS.find(p => p.id === showForm);
  const _modalName  = selectedPlan?.name
    || (selectedTP ? `${selectedTP.icon} بطولة حُجّة — ${selectedTP.label}` : "");
  const _modalPrice = selectedPlan?.price || selectedTP?.price || "";

  // ── Entitlement counter visibility ──────────────────────────────────────────
  // credits_expires_at is only cleared (→ null) when the plan expires WITH remaining
  // credits. If credits hit 0 through play, credits_expires_at stays set.
  // two_games has no timed expiry window — credits just persist until consumed.
  const _expiry      = currentUser?.credits_expires_at;
  const _planId      = currentUser?.last_plan_id;
  const _windowOpen  = _expiry ? new Date(_expiry) > new Date() : false; // timed plans only
  const _PLANS_WITH_COMMUNITY = ["two_games", "biweekly", "monthly"];
  // "any entitlement alive" — two_games: either pool > 0; timed plans: window still open
  const _anyActive = gameCredits > 0 || communityCredits > 0 || _windowOpen;
  // Show official counter while plan had official games AND any entitlement alive
  // (keeps official visible at 0 while community still active for two_games)
  const showOfficialCounter = (currentUser?.last_plan_games > 0) && _anyActive;
  // Show community counter:
  //   timed plans → while window open OR community > 0
  //   two_games   → while ANY pool > 0 (show 0 when other pool still active)
  const _twoGamesAnyActive  = _planId === "two_games" && (gameCredits > 0 || communityCredits > 0);
  const showCommunityCounter = _PLANS_WITH_COMMUNITY.includes(_planId) && (communityCredits > 0 || _windowOpen || _twoGamesAnyActive);
  const showBanner = showOfficialCounter || showCommunityCounter;

  return (
    <div className="min-h-screen relative overflow-hidden" dir="rtl">
      <style>{EMOJI_STYLE}</style>
      {/* Background image with overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${BG_IMAGE})` }}
      />
      <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(10,1,3,0.82) 0%, rgba(26,2,5,0.88) 50%, rgba(10,1,3,0.95) 100%)" }} />

      <div className="relative z-10 min-h-screen px-4 py-10">
        <div className="max-w-4xl mx-auto">

          {/* Back */}
          <button
            onClick={() => navigate("/")}
            className="mb-10 flex items-center gap-2 font-bold transition-all"
            style={{
              fontSize: "0.92rem",
              color: "rgba(241,225,148,0.80)",
              padding: "9px 20px",
              borderRadius: "13px",
              background: "rgba(241,225,148,0.06)",
              border: "1px solid rgba(241,225,148,0.22)",
              cursor: "pointer",
            }}
            onMouseEnter={e => { e.currentTarget.style.color = "#F1E194"; e.currentTarget.style.borderColor = "rgba(241,225,148,0.42)"; e.currentTarget.style.background = "rgba(241,225,148,0.10)"; }}
            onMouseLeave={e => { e.currentTarget.style.color = "rgba(241,225,148,0.80)"; e.currentTarget.style.borderColor = "rgba(241,225,148,0.22)"; e.currentTarget.style.background = "rgba(241,225,148,0.06)"; }}
          >
            ← رجوع
          </button>

          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-block bg-secondary/10 border border-secondary/20 text-secondary text-xs font-black px-4 py-1.5 rounded-full mb-4 tracking-widest uppercase">
              Premium
            </div>
            <h1
              className="font-black text-secondary text-4xl md:text-5xl mb-4"
              style={{ fontFamily: "Cairo, sans-serif", textShadow: "0 0 40px rgba(241,225,148,0.3)" }}
            >
              اختر خطتك المميزة
            </h1>
            <p className="text-secondary/50 text-base max-w-md mx-auto leading-relaxed">
              استمتع بكامل تجربة حُجّة — أسئلة لا تتكرر، جميع الفئات، وتتبع كامل للتقدم
            </p>
          </div>

          {/* Active plan banner — shown above cards when user has credits */}
          {showBanner && (
            <div className="bg-white/5 border border-secondary/20 rounded-2xl px-6 py-4 backdrop-blur-sm mb-8 flex items-center gap-4 flex-wrap">
              <span className="text-3xl">🎮</span>
              <div className="flex-1 min-w-0">
                <p className="text-secondary font-black text-base leading-tight">
                  {currentUser?.last_plan_name || "باقة نشطة"}
                </p>
                {/* Entitlement counters — visible even at 0 while plan window is open */}
                <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1">
                  {showOfficialCounter && (
                    <p className="text-white/40 text-xs">
                      🎮 الرسمية:{" "}
                      <span style={{ fontWeight: 800, fontSize: "0.82rem", color: "rgba(255,255,255,0.78)" }}>{gameCredits}</span>
                      {" "}<span className="text-white/40">متبقية</span>
                    </p>
                  )}
                  {showCommunityCounter && (
                    <p className="text-white/40 text-xs">
                      🌐 المجتمع:{" "}
                      <span style={{ fontWeight: 800, fontSize: "0.82rem", color: "rgba(255,255,255,0.78)" }}>{communityCredits}</span>
                      {" "}<span className="text-white/40">متبقية</span>
                    </p>
                  )}
                </div>
                {currentUser?.credits_expires_at && (
                  <p className="text-white/30 text-xs mt-0.5">
                    تنتهي {new Date(currentUser.credits_expires_at).toLocaleDateString("ar-SA")}
                  </p>
                )}
                {currentUser?.last_plan_games > 0 && gameCredits > 0 && (
                  <div className="mt-2">
                    <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-secondary transition-all"
                        style={{ width: `${Math.min(100, Math.round((gameCredits / currentUser.last_plan_games) * 100))}%` }}
                      />
                    </div>
                    <p className="text-white/25 text-xs mt-1">
                      استخدمت {currentUser.last_plan_games - gameCredits} من {currentUser.last_plan_games} ألعاب
                    </p>
                  </div>
                )}
              </div>
              <button
                onClick={() => navigate("/")}
                className="bg-secondary text-primary font-black px-5 py-2.5 rounded-xl text-sm hover:scale-105 transition-all shrink-0"
              >
                العب الحين ←
              </button>
            </div>
          )}

          {/* Pricing cards — always visible */}
          <>
            {/* Pricing cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
                {PLANS.map((plan) => (
                  <div
                    key={plan.id}
                    className={`relative flex flex-col rounded-3xl p-6 border transition-all duration-300 group
                      ${plan.popular
                        ? "border-secondary/70 bg-secondary/8 scale-[1.03] shadow-2xl"
                        : "border-white/10 bg-white/5 hover:border-secondary/30 hover:bg-white/8"
                      }`}
                    style={{
                      backdropFilter: "blur(16px)",
                      boxShadow: plan.popular
                        ? "0 0 50px rgba(241,225,148,0.15), 0 20px 60px rgba(0,0,0,0.5)"
                        : "0 8px 32px rgba(0,0,0,0.3)",
                    }}
                  >
                    {/* Popular badge */}
                    {plan.badge && (
                      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                        <span className="bg-secondary text-primary text-xs font-black px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap">
                          {plan.badge}
                        </span>
                      </div>
                    )}

                    {/* Plan name */}
                    <div className="mb-5">
                      <h3 className={`font-black text-xl mb-1 ${plan.popular ? "text-secondary" : "text-white/90"}`}>
                        {plan.emoji && <span className="pricing-emoji ml-1" aria-hidden="true">{plan.emoji}</span>}{plan.name}
                      </h3>
                      {plan.period && (
                        <p className="text-white/40 text-xs">{plan.period}</p>
                      )}
                      {plan.tagline && (
                        <p className="text-white/45 text-xs leading-relaxed mt-2">{plan.tagline}</p>
                      )}
                    </div>

                    {/* Price */}
                    <div className="mb-6">
                      <div className="flex items-baseline gap-1">
                        <span className={`text-4xl font-black ${plan.popular ? "text-secondary" : "text-white"}`}>
                          {plan.price}
                        </span>
                        <span className={`text-base font-bold ${plan.popular ? "text-secondary/70" : "text-white/50"}`}>
                          ر.س
                        </span>
                      </div>
                    </div>

                    {/* Features */}
                    <ul className="space-y-2.5 mb-6 flex-1">
                      {plan.features.map((f, i) => {
                        const match = f.match(/^(\p{Emoji_Presentation}|\p{Extended_Pictographic})\s*/u);
                        if (match) {
                          return (
                            <li key={i} className="text-white/70 leading-snug text-sm flex items-center gap-1.5">
                              <span className="pricing-emoji" aria-hidden="true">{match[0].trim()}</span>
                              <span>{f.slice(match[0].length)}</span>
                            </li>
                          );
                        }
                        return (
                          <li key={i} className="text-white/70 leading-snug text-sm">{f}</li>
                        );
                      })}
                    </ul>

                    {/* CTA */}
                    <button
                      data-testid={`upgrade-${plan.id}-btn`}
                      onClick={() => openForm(plan.id)}
                      className={`w-full font-black py-3.5 rounded-2xl transition-all duration-200
                        ${plan.popular
                          ? "bg-secondary text-primary hover:scale-[1.03] hover:shadow-[0_0_30px_rgba(241,225,148,0.4)]"
                          : "bg-white/10 text-white hover:bg-white/15 hover:scale-[1.02]"
                        }`}
                    >
                      {plan.cta || "اشترك الآن"}
                    </button>
                  </div>
                ))}
              </div>

              {/* Shared features — متوفر في جميع الباقات */}
              <div
                className="mt-8 mb-6 rounded-2xl px-6 py-5"
                style={{
                  background: "rgba(241,225,148,0.04)",
                  border: "1px solid rgba(241,225,148,0.12)",
                  backdropFilter: "blur(12px)",
                }}
              >
                <p className="text-center text-secondary/60 text-xs font-black uppercase tracking-widest mb-4">
                  متوفر في جميع الباقات
                </p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {SHARED_FEATURES.map(({ emoji, text }, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="pricing-emoji shrink-0" aria-hidden="true">{emoji}</span>
                      <span className="text-white/50 text-xs leading-snug">{text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Trust badge */}
              <p className="text-center text-white/25 text-xs flex items-center justify-center gap-2">
                <span>🔒</span>
                الدفع آمن عبر سلة
              </p>

              {/* ── Tournament Section — breaks out of max-w-4xl for genuine size ── */}
              <div
                ref={tournamentRef}
                style={{
                  position: "relative",
                  left: "50%", right: "50%",
                  marginLeft: "-50vw", marginRight: "-50vw",
                  width: "100vw",
                  padding: "0 1.5rem",
                  marginTop: "5rem",
                  marginBottom: "2.5rem",
                }}
              >
                <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
                  <div className="text-center mb-12">
                    <div className="inline-block bg-yellow-400/10 border border-yellow-400/25 text-yellow-300 text-sm font-black px-5 py-2 rounded-full mb-5 tracking-widest">
                      🏆 TOURNAMENT
                    </div>
                    <h2 className="font-black text-secondary mb-4" style={{ fontFamily: "Cairo, sans-serif", textShadow: "0 0 40px rgba(241,225,148,0.3)", fontSize: "clamp(2rem, 4vw, 3rem)" }}>
                      بطولات حُجّة
                    </h2>
                    <p className="text-white/40 text-base max-w-lg mx-auto leading-relaxed">
                      نظّم بطولة كاملة مع أصحابك — شجرة احترافية، مباريات متتالية، واحتفال روماني للبطل
                    </p>
                  </div>

                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                    {TOURNAMENT_PLANS.map((tp) => {
                      const isHighlighted = highlightPlanId === tp.id;
                      return (
                        <div
                          key={tp.id}
                          className={`relative flex flex-col rounded-3xl border transition-all duration-300 hover:scale-[1.02]${isHighlighted ? " tournament-highlight" : ""}`}
                          style={{
                            padding: "clamp(1.5rem, 2vw, 2rem)",
                            background: isHighlighted ? "rgba(241,225,148,0.10)" : tp.popular ? "rgba(241,225,148,0.08)" : "rgba(255,255,255,0.04)",
                            border: tp.popular && !isHighlighted ? "2px solid rgba(241,225,148,0.45)" : !isHighlighted ? "1px solid rgba(255,255,255,0.1)" : undefined,
                            backdropFilter: "blur(16px)",
                            boxShadow: tp.popular ? "0 0 50px rgba(241,225,148,0.12), 0 16px 50px rgba(0,0,0,0.5)" : "0 8px 32px rgba(0,0,0,0.35)",
                          }}
                        >
                          {isHighlighted && (
                            <div className="absolute -top-5 left-1/2 -translate-x-1/2 z-10">
                              <span className="bg-secondary text-primary text-xs font-black px-4 py-2 rounded-full shadow-lg whitespace-nowrap">
                                🏆 اخترت بطولة {tp.label}
                              </span>
                            </div>
                          )}
                          {tp.popular && !isHighlighted && (
                            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                              <span className="bg-secondary text-primary text-xs font-black px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap">الأكثر شيوعًا</span>
                            </div>
                          )}
                          {/* Icon */}
                          <div style={{ fontSize: "clamp(2.5rem, 4vw, 3.5rem)", marginBottom: "1rem" }}>{tp.icon}</div>
                          {/* Title */}
                          <div
                            className={`font-black mb-1.5 ${isHighlighted || tp.popular ? "text-secondary" : "text-white/90"}`}
                            style={{ fontFamily: "Cairo", fontSize: "clamp(1rem, 1.6vw, 1.35rem)" }}
                          >
                            بطولة {tp.label}
                          </div>
                          {/* Original price */}
                          <div className="text-white/30 text-sm line-through mb-1">{tp.original} ر.س</div>
                          {/* Price */}
                          <div className="flex items-baseline gap-1 mb-5">
                            <span
                              className={`font-black ${isHighlighted || tp.popular ? "text-secondary" : "text-white"}`}
                              style={{ fontSize: "clamp(2.2rem, 3.5vw, 3rem)" }}
                            >{tp.price}</span>
                            <span className="text-white/40 text-base">ر.س</span>
                          </div>
                          {/* Match count + rounds */}
                          <ul className="space-y-2 mb-6 flex-1">
                            <li className="text-white/70 text-base font-bold">🏆 {tp.matches} مباراة</li>
                            {tp.rounds.map((r, i) => (
                              <li key={i} className="text-white/40 text-sm">· {r}</li>
                            ))}
                          </ul>
                          {/* CTA */}
                          <button
                            data-testid={`upgrade-${tp.id}-btn`}
                            onClick={() => openForm(tp.id)}
                            className={`w-full font-black rounded-2xl transition-all duration-200
                              ${isHighlighted ? "bg-secondary text-primary hover:scale-105 hover:shadow-[0_0_24px_rgba(241,225,148,0.4)]" : tp.popular ? "bg-secondary text-primary hover:scale-105" : "bg-white/10 text-white hover:bg-white/15"}`}
                            style={{ padding: "clamp(0.75rem, 1.2vw, 1rem)", fontSize: "clamp(0.9rem, 1.2vw, 1.05rem)" }}
                          >
                            شراء البطولة
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-center text-white/20 text-sm mt-8 flex items-center justify-center gap-2">
                    <span>🔒</span>
                    كل بطولة تمنحك رصيد كافٍ لإكمال جميع مبارياتها
                  </p>
                </div>
              </div>

              {/* FAQ Accordion */}
              <div className="mt-16">
                <h2
                  className="text-center text-secondary font-black text-2xl mb-3"
                  style={{ fontFamily: "Cairo, sans-serif" }}
                >
                  كيف تعمل الباقات؟
                </h2>
                <p className="text-center text-white/30 text-sm mb-10">كل ما تحتاج معرفته قبل الاشتراك</p>

                <div className="space-y-3 max-w-2xl mx-auto">
                  {[
                    {
                      emoji: "❓",
                      q: "كيف يتم احتساب المباراة؟",
                      a: "🎮 كل مباراة كاملة تستهلك لعبة واحدة فقط، مهما كان عدد اللاعبين.",
                    },
                    {
                      emoji: "❓",
                      q: "كم عدد اللاعبين؟",
                      a: "👥 يمكنك اللعب بفريقين أو أكثر، وكل فريق يضم أي عدد من اللاعبين.",
                    },
                    {
                      emoji: "❓",
                      q: "هل جميع الفئات متاحة؟",
                      a: "📚 نعم، جميع الباقات المدفوعة تمنحك وصولاً كاملاً لجميع الفئات الحالية، بالإضافة إلى أي فئات جديدة تتم إضافتها مستقبلاً.",
                    },
                    {
                      emoji: "❓",
                      q: "هل تتكرر الأسئلة؟",
                      a: "🧠 يتم اختيار الأسئلة باستخدام نظام ذكي يقلل التكرار قدر الإمكان، مع إعطاء أولوية للأسئلة الأقل ظهوراً.",
                    },
                    {
                      emoji: "❓",
                      q: "ماذا يحدث عند انتهاء الاشتراك؟",
                      a: "⏳ عند انتهاء مدة الباقة تنتهي الألعاب المتبقية، ويمكنك تجديد الاشتراك في أي وقت.",
                    },
                    {
                      emoji: "❓",
                      q: "هل أستطيع إنشاء فئات خاصة؟",
                      a: "🌍 نعم، يمكن للمشتركين إنشاء فئاتهم الخاصة ونشرها في قسم المجتمع ليستمتع بها الجميع.",
                    },
                    {
                      emoji: "🏆",
                      q: "ما الفرق بين المباريات الرسمية ومباريات المجتمع؟",
                      a: "🎮 المباريات الرسمية تستخدم فئات حُجّة المعتمدة — أسئلة مُراجَعة ومُنقَّحة من الفريق. 🌐 مباريات المجتمع تستخدم فئات أنشأها لاعبون آخرون — تنوع أكبر وتجديد مستمر.",
                    },
                    {
                      emoji: "⚡️",
                      q: "متى تنتهي مباريات المجتمع؟",
                      a: "⚡️ باقة لعبتان: مباريات المجتمع لا تنتهي بمدة زمنية — تبقى حتى تستهلكها. 📅 باقتَي أسبوعان وشهري: تنتهي مباريات المجتمع مع انتهاء مدة الباقة.",
                    },
                    {
                      emoji: "🔄",
                      q: "ماذا يحدث لرصيدي إذا اشتريت باقة ثانية قبل انتهاء الأولى؟",
                      a: "➕ المباريات الرسمية والمجتمعية تُضاف فوق رصيدك الحالي. ⏱️ أما مدة الباقة الجديدة فتبدأ من تاريخ الشراء — تأكد من استهلاك رصيدك قبل الانتهاء.",
                    },
                    {
                      emoji: "📊",
                      q: "أين أرى رصيد مبارياتي؟",
                      a: "📊 يظهر رصيدك في أعلى هذه الصفحة، وكذلك كاختصار صغير في الصفحة الرئيسية بجانب اسمك. انقر عليه للانتقال مباشرة إلى هنا.",
                    },
                    {
                      emoji: "🎟️",
                      q: "هل يمكنني استخدام كود خصم؟",
                      a: "🎟️ نعم، إذا كان لديك كود خصم من شريك أو مروّج حُجّة، أدخله في الحقل المخصص داخل نافذة الدفع قبل المتابعة. تأكد من إعادة إدخاله في صفحة الدفع بسلة لتطبيق الخصم الفعلي.",
                    },
                    {
                      emoji: "🔒",
                      q: "هل الدفع آمن؟",
                      a: "🔒 يتم الدفع بالكامل عبر منصة سلة — إحدى أكبر منصات التجارة الإلكترونية في السعودية. لا يتم حفظ أي بيانات دفع على خوادمنا.",
                    },
                  ].map(({ emoji, q, a }, i) => {
                    const isOpen = openFaq === i;
                    return (
                      <div
                        key={i}
                        className="rounded-2xl overflow-hidden transition-all duration-300"
                        style={{
                          background: isOpen
                            ? "rgba(241,225,148,0.06)"
                            : "rgba(255,255,255,0.03)",
                          border: isOpen
                            ? "1px solid rgba(241,225,148,0.28)"
                            : "1px solid rgba(241,225,148,0.08)",
                          backdropFilter: "blur(16px)",
                          boxShadow: isOpen
                            ? "0 0 30px rgba(241,225,148,0.06)"
                            : "none",
                        }}
                      >
                        {/* Question row */}
                        <button
                          onClick={() => setOpenFaq(isOpen ? null : i)}
                          className="w-full flex items-center justify-between gap-4 px-5 py-4 text-right transition-colors"
                          style={{ fontFamily: "Cairo, sans-serif", background: "none", border: "none", cursor: "pointer" }}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <span
                              className="pricing-emoji shrink-0"
                              aria-hidden="true"
                              style={{ fontSize: "1.1em", opacity: isOpen ? 1 : 0.6 }}
                            >{emoji}</span>
                            <span
                              className="font-bold text-sm leading-snug"
                              style={{ color: isOpen ? "#F1E194" : "rgba(255,255,255,0.75)" }}
                            >{q}</span>
                          </div>
                          {/* Arrow */}
                          <span
                            aria-hidden="true"
                            style={{
                              color: "rgba(241,225,148,0.5)",
                              fontSize: "0.75rem",
                              flexShrink: 0,
                              display: "inline-block",
                              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                              transition: "transform 300ms ease",
                            }}
                          >▼</span>
                        </button>

                        {/* Answer — smooth expand */}
                        <div
                          style={{
                            maxHeight: isOpen ? "200px" : "0px",
                            overflow: "hidden",
                            transition: "max-height 300ms ease, opacity 300ms ease",
                            opacity: isOpen ? 1 : 0,
                          }}
                        >
                          <p
                            className="text-sm leading-relaxed px-5 pb-5"
                            style={{
                              color: "rgba(255,255,255,0.50)",
                              fontFamily: '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji","Cairo",sans-serif',
                              paddingTop: "2px",
                            }}
                          >{a}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
          </>

          {/* Salla checkout confirmation modal — unified for all plans */}
          {showForm && (
            <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div
                className="border border-secondary/20 rounded-3xl p-7 max-w-sm w-full"
                style={{ background: "rgba(15,1,2,0.97)", boxShadow: "0 0 60px rgba(0,0,0,0.8)" }}
              >
                {/* Product name + price */}
                <div className="mb-6">
                  <h3 className="text-secondary font-black text-xl mb-1">{_modalName}</h3>
                  <p className="text-white/60 text-base font-bold">{_modalPrice} ر.س</p>
                </div>

                {/* Instructions */}
                <div className="space-y-3 mb-6">
                  <div className="rounded-xl px-4 py-3" style={{ background: "rgba(241,225,148,0.05)", border: "1px solid rgba(241,225,148,0.12)" }}>
                    <p className="text-xs leading-relaxed" style={{ color: "rgba(255,255,255,0.55)", fontFamily: "Cairo, sans-serif" }}>
                      <span style={{ marginLeft: "4px" }}>🍎</span>
                      <span className="font-bold" style={{ color: "rgba(241,225,148,0.75)" }}>للدفع عبر Apple Pay:</span>{" "}
                      أضف المنتج إلى السلة أولًا، ثم افتح السلة وأكمل عملية الدفع منها.
                    </p>
                  </div>
                  <div className="rounded-xl px-4 py-3" style={{ background: "rgba(241,225,148,0.05)", border: "1px solid rgba(241,225,148,0.12)" }}>
                    <p className="text-xs leading-relaxed" style={{ color: "rgba(255,255,255,0.55)", fontFamily: "Cairo, sans-serif" }}>
                      <span style={{ marginLeft: "4px" }}>🎟️</span>
                      بعد إتمام الشراء، انسخ كود التفعيل من تفاصيل طلبك في سلة وأدخله في{" "}
                      <span className="font-bold" style={{ color: "rgba(241,225,148,0.75)" }}>«أكواد التفعيل»</span>{" "}
                      داخل حُجّة.
                    </p>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowForm(null)}
                    className="flex-1 border border-white/15 text-white/50 py-3 rounded-xl font-bold hover:border-white/30 hover:text-white/70 transition-all"
                  >
                    إلغاء
                  </button>
                  <button
                    data-testid="paylink-pay-btn"
                    onClick={handleUpgrade}
                    disabled={loading === showForm}
                    className="flex-1 bg-secondary text-primary font-black py-3 rounded-xl hover:scale-105 transition-all disabled:opacity-50"
                  >
                    {loading === showForm ? "⏳ جاري..." : "متابعة الدفع في سلة"}
                  </button>
                </div>
                <p className="text-white/20 text-xs text-center mt-4">
                  سيتم تحويلك إلى صفحة الدفع الآمنة في سلة
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
